import { Award, Shield, AlertCircle, HelpCircle } from 'lucide-react';
import { ORG_CODES, getOrgLabel, deriveOrgCode } from '@/utils/organizations';

/**
 * OrganizationBadge — pokazuje pochodzenie rodowodu encji.
 * Użycie:
 *   <OrganizationBadge entity={kennel} />   // auto-derive z is_fci / organization_type
 *   <OrganizationBadge code="fci_zkwp" />   // manual
 *
 * Kolor:
 *   🥇 fci_zkwp → emerald (wyróżniony — „złoty standard")
 *   🥈 inne organizacje → blue (neutralny)
 *   ⚪ bez rodowodu → gray
 *   ❓ nie podano → gray subtle
 *
 * Props:
 *   size: 'sm' | 'md' (default 'sm')
 *   showIcon: bool (default true)
 *   className: additional classes
 */
export default function OrganizationBadge({ entity, code, size = 'sm', showIcon = true, className = '' }) {
  const orgCode = code || deriveOrgCode(entity);
  const label = getOrgLabel(orgCode);

  const variant = (() => {
    if (orgCode === ORG_CODES.FCI_ZKWP) {
      return {
        bg: 'bg-emerald-500/15',
        border: 'border-emerald-500/40',
        text: 'text-emerald-300',
        icon: Award,
      };
    }
    if (orgCode === ORG_CODES.NO_PEDIGREE) {
      return {
        bg: 'bg-gray-500/10',
        border: 'border-gray-500/30',
        text: 'text-gray-400',
        icon: AlertCircle,
      };
    }
    if (orgCode === ORG_CODES.UNKNOWN) {
      return {
        bg: 'bg-white/5',
        border: 'border-white/10',
        text: 'text-gray-500',
        icon: HelpCircle,
      };
    }
    // Inne zarejestrowane organizacje — neutralny niebieski
    return {
      bg: 'bg-blue-500/15',
      border: 'border-blue-500/40',
      text: 'text-blue-300',
      icon: Shield,
    };
  })();

  const Icon = variant.icon;
  const sizeCls =
    size === 'md'
      ? 'px-2.5 py-1 text-xs gap-1.5'
      : 'px-2 py-0.5 text-[10px] gap-1';
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';

  return (
    <span
      data-testid={`org-badge-${orgCode}`}
      className={`inline-flex items-center rounded-full border font-medium whitespace-nowrap ${variant.bg} ${variant.border} ${variant.text} ${sizeCls} ${className}`}
      title={`Rodowód: ${label}`}
    >
      {showIcon && <Icon className={iconSize} />}
      {label}
    </span>
  );
}
